// External Libraries
import React from "react";
import moment from "moment";
import { toast as alert } from "react-toastify";
import * as Icon from "react-feather";
import Modal from "react-responsive-modal";

// Services
import BookkeepingService from "../../../../services/BookkeepingService";
import PlaidService from "../../../../services/PlaidService";

// Common Components
import WComponent from "../../../common/WComponent";
import Loader from "../../../common/loader/Loader";
import Button from "../../../common/button/Button";
import DateSelector from "../../../common/date_selector/DateSelector";
import Checkbox from "../../../common/checkbox/Checkbox";
import TextFieldInput from "../../../common/text_field_input/TextFieldInput";
import SearchableDropdown from "../../../common/searchable_dropdown/SearchableDropdown";

// Components
import BookkeepingTable from "./BookkeepingTable";

class BookkeepingTool extends WComponent {
	constructor(props) {
		super(props);

		this.state = {
			// Input State
			files: [],
			cwFiles: [],
			endDate: new Date(),
			startDate: new Date(),

			// Data State
			connectedAccounts: [],
			transactions: [],

			// UI State
			loading: false,

			// Wizard State
			wizardOpen: true,
			wizardStep: 1,

			// Setup Config (passed to BookkeepingTable as initial values)
			industry: "",
			province: "",
			hasTax: true,
		};
	}

	onGenerateBookkeepingClicked = async () => {
		let { clientId } = this.props;
		let { endDate, startDate, connectedAccounts, files } = this.state;

		const formattedStartDate = moment(startDate).format("YYYY-MM-DD");
		const formattedEndDate = moment(endDate).format("YYYY-MM-DD");

		await this.update({ loading: true });

		try {
			let allTransactions = { accounts: [], transactions: [] };

			if (files.length > 0) {
				let totalInvalid = 0;
				for (let file of files) {
					let { transactions, invalidCount } = await BookkeepingService.extractTransactionsFromCsv({ file });
					allTransactions.transactions.push(...transactions);
					totalInvalid += invalidCount || 0;
				}
				if (totalInvalid > 0) {
					alert.error(`${totalInvalid} row(s) have missing or unrecognizable date or amount values. Please fix the CSV file(s) and re-import.`);
					await this.update({ loading: false, files: [] });
					return;
				}
			} else {
				for (let account of connectedAccounts) {
					if (account.isChecked) {
						let { accounts, transactions } = await PlaidService.fetchAccountsAndTransactions({
							startDate: formattedStartDate,
							endDate: formattedEndDate,
							plaidAccountId: account.id,
							clientId
						});
						allTransactions.accounts.push(accounts[0]);
						allTransactions.transactions.push(...transactions);
					}
				}
			}

			let accountMap = allTransactions.accounts.reduce((acc, account) => {
				acc[account.account_id] = account.name;
				return acc;
			}, {});

			let transactions = allTransactions.transactions.map(transaction => ({
				date: transaction.date,
				description: transaction.name || transaction.description || "",
				merchant: transaction.merchant_name,
				amount: transaction.amount,
				accountName: accountMap[transaction.account_id] || transaction.bankAccountName || "",
				selected: false,
				classification: null,
				tax: ""
			}));

			const { industry, province, hasTax } = this.state;
			localStorage.setItem(`bookkeeping_session_${clientId}`, JSON.stringify({ transactions, industry, province, hasTax }));

			await this.update({
				transactions,
				loading: false,
				wizardOpen: false,
			});
		} catch (error) {
			console.error("Error fetching transactions:", error);
			await this.update({ loading: false });
		}
	};

	onUpload = async e => {
		const selectedFiles = Array.from(e.target.files);
		await this.update({ files: selectedFiles });
	};

	componentDidMount = async () => {
		let { clientId } = this.props;

		const savedSession = localStorage.getItem(`bookkeeping_session_${clientId}`);
		if (savedSession) {
			try {
				const { transactions, industry, province, hasTax } = JSON.parse(savedSession);
				if (transactions && transactions.length > 0) {
					await this.update({ transactions, industry, province, hasTax, wizardOpen: false });
				}
			} catch (_) {}
		}

		let result = await PlaidService.fetchAccounts({ searchTerm: "", clientId });
		let bankAccounts = result.map(account => ({
			id: account.id,
			title: account.name,
			name: account.name,
			isChecked: false
		}));
		await this.update({ connectedAccounts: bankAccounts });
	};

	onStartDateChange = async startDate => { await this.update({ startDate }); };
	onEndDateChange = async endDate => { await this.update({ endDate }); };

	checkBoxSelected = async index => {
		let { connectedAccounts } = this.state;
		connectedAccounts[index].isChecked = !connectedAccounts[index].isChecked;
		await this.update({ connectedAccounts });
	};

	onTransactionsUpdate = async updatedTransactions => {
		await this.update({ transactions: updatedTransactions });
		const { clientId } = this.props;
		const { industry, province, hasTax } = this.state;
		localStorage.setItem(`bookkeeping_session_${clientId}`, JSON.stringify({ transactions: updatedTransactions, industry, province, hasTax }));
	};

	// Wizard handlers
	onWizardNext = () => this.update({ wizardStep: this.state.wizardStep + 1 });
	onWizardBack = () => this.update({ wizardStep: this.state.wizardStep - 1 });
	onWizardClose = () => this.update({ wizardOpen: false });
	onWizardOpen = () => {
		const { clientId } = this.props;
		localStorage.removeItem(`bookkeeping_session_${clientId}`);
		this.update({ wizardOpen: true, wizardStep: 1, files: [], cwFiles: [], transactions: [] });
	};
	onWizardCwUpload = async e => { await this.update({ cwFiles: Array.from(e.target.files) }); };
	onWizardChange = async e => { await this.update({ [e.target.name]: e.target.value }); };
	onWizardHasTaxToggle = () => this.update({ hasTax: !this.state.hasTax });
	onWizardProvinceSelect = province => this.update({ province: province.name });

	renderWizardStep1 = () => {
		const { files, connectedAccounts } = this.state;
		const hasConnectedAccounts = connectedAccounts.length > 0;

		return (
			<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
				{/* CSV format instructions */}
				<div style={{ background: "#f0f4ff", borderRadius: 8, padding: "16px 20px", border: "1px solid #d0d8f0" }}>
					<div style={{ fontWeight: 600, fontSize: 15, marginBottom: 10, color: "#395395" }}>
						CSV File Format
					</div>
					<p style={{ margin: "0 0 8px", fontSize: 14, color: "#444" }}>
						Name each file after its bank account (e.g. <strong>RBC Chequing.csv</strong>). Upload one file per account.
					</p>
					<p style={{ margin: "0 0 10px", fontSize: 14, color: "#444" }}>
						Each file must have exactly <strong>3 columns</strong> in this order:
					</p>
					<div style={{ display: "grid", gridTemplateColumns: "1fr 3fr 1fr", gap: "4px 12px", background: "white", borderRadius: 6, padding: 12, border: "1px solid #dde", fontSize: 13 }}>
						<div style={{ fontWeight: 700, color: "#395395", paddingBottom: 6, borderBottom: "1px solid #eee" }}>Date</div>
						<div style={{ fontWeight: 700, color: "#395395", paddingBottom: 6, borderBottom: "1px solid #eee" }}>Description</div>
						<div style={{ fontWeight: 700, color: "#395395", paddingBottom: 6, borderBottom: "1px solid #eee" }}>Amount</div>
						<div style={{ color: "#666" }}>2024-01-15</div>
						<div style={{ color: "#666" }}>Office Supplies – Staples</div>
						<div style={{ color: "#666" }}>150.00</div>
						<div style={{ color: "#666" }}>2024-01-22</div>
						<div style={{ color: "#666" }}>Monthly Software Subscription</div>
						<div style={{ color: "#666" }}>49.99</div>
					</div>
					<p style={{ margin: "10px 0 0", fontSize: 13, color: "#666" }}>
						Tip: You can upload multiple CSV files at once — one per bank account.
					</p>
				</div>

				{/* File upload */}
				<div>
					<label style={{ cursor: "pointer", display: "inline-block" }}>
						<Button
							text={files.length > 0 ? `${files.length} file${files.length > 1 ? "s" : ""} selected — Change` : "Select CSV Files"}
							view="secondary"
							icon={<Icon.Upload size={16} />}
						/>
						<input type="file" multiple accept=".csv" onChange={this.onUpload} style={{ display: "none" }} />
					</label>

					{files.length > 0 && (
						<div style={{ marginTop: 10 }}>
							{files.map((f, i) => (
								<div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: "1px solid #f0f0f0", fontSize: 14 }}>
									<Icon.FileText size={15} color="#395395" />
									<span>{f.name}</span>
								</div>
							))}
						</div>
					)}
				</div>

				{/* Connected Plaid accounts (if any) */}
				{hasConnectedAccounts && (
					<div>
						<div style={{ display: "flex", alignItems: "center", gap: 12, margin: "4px 0", color: "#aaa", fontSize: 13 }}>
							<div style={{ flex: 1, height: 1, background: "#eee" }} />
							<span>or import from connected accounts</span>
							<div style={{ flex: 1, height: 1, background: "#eee" }} />
						</div>
						<div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
							{connectedAccounts.map((account, index) => (
								<Checkbox
									key={index}
									title={account.title}
									checked={account.isChecked}
									onChange={() => this.checkBoxSelected(index)}
								/>
							))}
						</div>
					</div>
				)}
			</div>
		);
	};

	renderWizardStep2 = () => {
		const { cwFiles } = this.state;

		return (
			<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
				<div style={{ background: "#f0f4ff", borderRadius: 8, padding: "16px 20px", border: "1px solid #d0d8f0" }}>
					<div style={{ fontWeight: 600, fontSize: 15, marginBottom: 10, color: "#395395" }}>
						CaseWare Reference Files
					</div>
					<p style={{ margin: "0 0 10px", fontSize: 14, color: "#444" }}>
						Upload your CaseWare <strong>.dbf</strong> files to auto-classify transactions and import your prior year trial balance. Look for the following files:
					</p>
					<ul style={{ margin: "0 0 10px", paddingLeft: 20, fontSize: 14, color: "#444", lineHeight: "1.8" }}>
						<li><strong>am.dbf</strong></li>
						<li><strong>bl.dbf</strong></li>
						<li>
							<strong>gl.dbf</strong> <span style={{ color: "#888", fontStyle: "italic" }}>(if available)</span>
							<span style={{ display: "block", fontSize: 13, color: "#555", marginTop: 2 }}>
								Including <strong>gl.dbf</strong> significantly increases the accuracy of account allocations.
							</span>
						</li>
					</ul>
					<p style={{ margin: "0 0 8px", fontSize: 14, color: "#444" }}>Typically found at:</p>
					<div style={{ background: "white", borderRadius: 6, padding: "10px 14px", border: "1px solid #dde", fontSize: 13, fontFamily: "monospace", color: "#555" }}>
						C:\Program Files (x86)\CaseWare\Data\ClientName\
					</div>
					<p style={{ margin: "10px 0 0", fontSize: 13, color: "#666" }}>
						This step is optional — you can skip it and upload later from the table header.
					</p>
				</div>

				<div>
					<label style={{ cursor: "pointer", display: "inline-block" }}>
						<Button
							text={cwFiles.length > 0 ? `${cwFiles.length} file${cwFiles.length > 1 ? "s" : ""} selected — Change` : "Select .dbf Files"}
							view="secondary"
							icon={<Icon.Upload size={16} />}
						/>
						<input type="file" multiple accept=".dbf" onChange={this.onWizardCwUpload} style={{ display: "none" }} />
					</label>

					{cwFiles.length > 0 && (
						<div style={{ marginTop: 10 }}>
							{cwFiles.map((f, i) => (
								<div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: "1px solid #f0f0f0", fontSize: 14 }}>
									<Icon.FileText size={15} color="#395395" />
									<span>{f.name}</span>
								</div>
							))}
						</div>
					)}
				</div>
			</div>
		);
	};

	renderWizardStep3 = () => {
		const { industry, hasTax, startDate, endDate, connectedAccounts } = this.state;
		const needsDates = connectedAccounts.some(a => a.isChecked);

		return (
			<div style={{ display: "flex", flexDirection: "column", gap: 16, minHeight: 360 }}>
				<p style={{ margin: 0, color: "#666", fontSize: 14 }}>
					These settings are used for automatic transaction classification. You can adjust them later in the table header.
				</p>
				<TextFieldInput title="Industry" name="industry" value={industry} onChange={this.onWizardChange} autoComplete="off" />
				<SearchableDropdown title="Select a province" type="provinces" onSelect={this.onWizardProvinceSelect} firstFocus showAll />
				<Checkbox title="Taxes applicable" checked={hasTax} onChange={this.onWizardHasTaxToggle} />
				{needsDates && (
					<>
						<DateSelector title="Start Date" selectedDate={startDate ? new Date(startDate) : undefined} onDateSelect={this.onStartDateChange} />
						<DateSelector title="End Date" selectedDate={endDate ? new Date(endDate) : undefined} onDateSelect={this.onEndDateChange} />
					</>
				)}
			</div>
		);
	};

	renderWizard = () => {
		const { wizardOpen, wizardStep, files, connectedAccounts, loading } = this.state;
		const canProceedStep1 = files.length > 0 || connectedAccounts.some(a => a.isChecked);

		const stepTitles = {
			1: "Import Bank Statements",
			2: "Caseware References",
			3: "Configure Settings",
		};

		return (
			<Modal styles={{ modal: { width: 580, overflow: "visible" } }} open={wizardOpen} onClose={this.onWizardClose} center>
				<div className="modal-title" style={{ paddingBottom: 12, borderBottom: "1px solid #e8e8e8", marginBottom: 20 }}>
					<span>{stepTitles[wizardStep]}</span>
					<div style={{ fontSize: 13, fontWeight: 400, color: "#999", marginTop: 4 }}>Step {wizardStep} of 3</div>
				</div>

				<div className="modal-content">
					{wizardStep === 1 && this.renderWizardStep1()}
					{wizardStep === 2 && this.renderWizardStep2()}
					{wizardStep === 3 && this.renderWizardStep3()}
				</div>

				<div className="modal-footer">
					{wizardStep > 1 && <Button text="Back" view="secondary" onClick={this.onWizardBack} />}
					{wizardStep === 1 && (
						<Button text="Next" onClick={this.onWizardNext} disabled={!canProceedStep1} />
					)}
					{wizardStep === 2 && (
						<>
							<Button text="Skip" view="secondary" onClick={this.onWizardNext} />
							<Button text="Next" onClick={this.onWizardNext} />
						</>
					)}
					{wizardStep === 3 && (
						<Button
							text={loading ? "Generating..." : "Generate Bookkeeping"}
							onClick={this.onGenerateBookkeepingClicked}
							// icon={<Icon.Play size={16} />}
							disabled={loading}
						/>
					)}
				</div>
			</Modal>
		);
	};

	render() {
		const { loading, transactions, wizardOpen, industry, province, hasTax, cwFiles } = this.state;
		const { clientId } = this.props;

		if (loading) {
			return (
				<div className="container-loader">
					<Loader />
				</div>
			);
		}

		return (
			<div>
				{this.renderWizard()}

				{transactions.length > 0 ? (
					<div className="container">
						<BookkeepingTable
							clientId={clientId}
							transactions={transactions}
							onTransactionsUpdate={this.onTransactionsUpdate}
							initialIndustry={industry}
							initialProvince={province}
							initialHasTax={hasTax}
							initialCwFiles={cwFiles}
							onNewImport={this.onWizardOpen}
						/>
					</div>
				) : (
					!wizardOpen && (
						<div className="container-empty">
							<Icon.BookOpen size={48} color="#dfdfdf" />
							<p>No transactions loaded.</p>
							<Button text="Import Bank Statements" onClick={this.onWizardOpen} icon={<Icon.Upload size={16} />} />
						</div>
					)
				)}
			</div>
		);
	}
}

export default BookkeepingTool;
